import template from './template';
import styles from './template.css';
import {AoflElement, customElement, property} from '@aofl/element';
import {storeInstance} from '@aofl/store/dist/legacy';
import {mapStatePropertiesMixin} from '@aofl/map-state-properties-mixin';
import {uuid} from '@aofl/uuid';
import '../ems-dropdown-sdo';
import {sdoNs} from '../../../constants-enumerate';

/**
 * @summary Dropdown
 * @extends {AoflElement}
 */
@customElement('ems-dropdown')
class Dropdown extends mapStatePropertiesMixin(AoflElement) {
  /**
   * Creates an instance of Dropdown
   */
  constructor() {
    super();
    this.storeInstance = storeInstance;
  }

  /**
   *
   * @readonly
   */
  static is = 'ems-dropdown';

  @property({type: Boolean, reflect: true})
  open = false;

  @property({type: Boolean})
  openOnSelect = false;

  @property({type: Boolean})
  disabled = false;

  @property({type: String})
  dropdownId = '';

  @property({type: Boolean})
  ['full-width'] = false;

  @property({type: Array, attribute: true})
  styles = [];

  @property({type: Boolean})
  dropdownOpened = false;

  @property({type: Number})
  numberOfButtons = 0;

  @property({type: String, attribute: 'data-bi-id'})
  dataBiId = '';

  /**
   *
   */
  connectedCallback() {
    super.connectedCallback();
    this.dropdownId = this.id || uuid();
    this.addEventListener('keydown', this.keydownHandler);
    this.addEventListener('change', this.listChangeHandler, true);
    this.addToState();
  }

  /**
   *
   */
  addToState() {
    const state = this.storeInstance.getState();
    const dropdowns = state[sdoNs.DROPDOWN];
    const noDropdowns = Object.keys(dropdowns).length === 0;

    if (noDropdowns) {
      window.addEventListener('click', (e) => {
        const path = e.composedPath();
        let clickedDropdown = false;
        let clickedDropdownItem = false;
        for (let i = 0; i < path.length; i++) {
          const className = path[i].className || '';
          if (path[i].nodeName === 'EMS-DROPDOWN') {
            clickedDropdown = true;
            break;
          } else if (className.indexOf('dropdown-item') > -1) {
            clickedDropdownItem = true;
          }
        }
        if (!clickedDropdown || clickedDropdownItem) {
          this.closeAllDropdowns();
        }
      });
    }

    this.storeInstance.commit({
      namespace: sdoNs.DROPDOWN,
      mutationId: 'add',
      payload: this.dropdownId
    });
  }

  setButtonIds() {
    const buttonElements = this.querySelectorAll('button.dropdown-item');
    buttonElements.forEach(function(item, index) {
      item.setAttribute('btnIndex', `${index}`);
    });
    this.numberOfButtons = buttonElements.length;
  }

  focusNextListItem(buttonId, direction) {
    if (direction === 'down') {
      const currentActiveElementIsNotLastItem = buttonId < this.numberOfButtons - 1;
      if (currentActiveElementIsNotLastItem) {
        const nextListItemId = parseInt(buttonId) + 1;
        this.querySelector(`button[btnIndex="${nextListItemId}"`).focus();
      } else {
        // Reset to first option
        const nextListItemId = 0;
        this.querySelector(`button[btnIndex="${nextListItemId}"`).focus();
      }
    } else if (direction === 'up') {
      const currentActiveElementIsNotFirstItem = buttonId > 0;
      if (currentActiveElementIsNotFirstItem) {
        const nextListItemId = parseInt(buttonId) - 1;
        this.querySelector(`button[btnIndex="${nextListItemId}"`).focus();
      } else {
        // Go to last option
        const nextListItemId = this.numberOfButtons - 1;
        this.querySelector(`button[btnIndex="${nextListItemId}"`).focus();
      }
    }
  }

  /**
   * @param {Event} e
   */
  keydownHandler(e) {
    if (this.disabled) return;
    /* istanbul ignore else */
    // ' ' is the SpaceBar
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowUp') {
      this.storeInstance.commit({
        namespace: sdoNs.DROPDOWN,
        mutationId: 'open',
        payload: this.dropdownId
      });
      // Only need to run this once on initial dropdown open
      if(!this.dropdownOpened) {
        this.dispatchEvent(new CustomEvent('on-open'));
        this.dropdownOpened = true;
      } else {
        if(e.key === 'ArrowDown' && this.numberOfButtons > 0) {
          //-1 if they have not focused on the dropdown yet go to first element
          const btnId =  e.target.getAttribute('btnIndex') || -1;
          this.focusNextListItem(btnId, 'down');
        } else if (e.key === 'ArrowUp' && this.numberOfButtons > 0) {
          //-1 if they have not focused on the dropdown yet go to first element
          const btnId =  e.target.getAttribute('btnIndex')|| -1;
          this.focusNextListItem(btnId, 'up');
        }
      }
    } else if (e.key === 'Escape') {
      this.storeInstance.commit({
        namespace: sdoNs.DROPDOWN,
        mutationId: 'close',
        payload: this.dropdownId
      });
    }
  }

  /**
   * @param {Event} e
   */
  listChangeHandler(e) {
    const listChanging = e.target.localName === 'aofl-select-list' ||
      e.target.localName === 'aofl-multiselect-list';
    if (!this.openOnSelect && listChanging) {
      this.storeInstance.commit({
        namespace: sdoNs.DROPDOWN,
        mutationId: 'close',
        payload: this.dropdownId
      });
    }
  }

  /**
   *
   */
  closeAllDropdowns() {
    this.storeInstance.commit({
      namespace: sdoNs.DROPDOWN,
      mutationId: 'closeAll'
    });
  }

  /**
   * @param {Event} e
   */
  toggle(e) {
    if (this.disabled) return;
    const state = this.storeInstance.getState();
    const dropdowns = state[sdoNs.DROPDOWN];
    const mutationId = dropdowns[this.dropdownId] ? 'close' : 'open';
    const event = dropdowns[this.dropdownId] ? 'on-close' : 'on-open';
    this.storeInstance.commit({
      namespace: sdoNs.DROPDOWN,
      mutationId,
      payload: this.dropdownId
    });
    this.dispatchEvent(new CustomEvent(event));
  }

  /**
   *
   */
  mapStateProperties() {
    const state = this.storeInstance.getState();
    this.open = state[sdoNs.DROPDOWN][this.dropdownId];
    this.setButtonIds();
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render(template, [styles, this.styles]);
  }

  /**
  /* istanbul ignore next */
  disconnectedCallback() {
    this.removeEventListener('keydown', this.keydownHandler);
    this.removeEventListener('change', this.listChangeHandler);
    document.removeEventListener('click', this.closeAllDropdowns);
    super.disconnectedCallback();
  }
}

export default Dropdown;
